/**
 * WhatsApp Backend API helpers (whatsapp_API/openapi.json).
 * Browser code calls whatsapp_microservice **`fetch`** only where same-origin/CORS allows;
 * **`refreshWhatsAppMessageMedia`** goes through Admin Flask `/whatsapp/message-media/refresh`.
 */

import { API_CONFIG } from "config/api.config";
import { api } from "utils/api";
import { formatPhoneNumber } from "utils/phoneUtils";
import { getStoredUserLogin } from "utils/sessionAuth";

const joinUrl = (base, path) => {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "/");
  return `${b}${p.startsWith("/") ? p : `/${p}`}`;
};

/** Same console logging shape as utils/api.js fetchInstance; keys redacted for safety. */
const redactWhatsAppHeadersForLog = (headers) => ({
  ...headers,
  "x-api-key": "***",
  "X-API-Key": "***",
});

const logWhatsAppRequest = (fullUrl, method, headers, bodyForLog) => {
  console.group("🌐 API Request");
  console.log("URL:", fullUrl);
  console.log("Method:", method);
  console.log("Headers:", redactWhatsAppHeadersForLog(headers));
  if (bodyForLog !== undefined && bodyForLog !== null && bodyForLog !== "") {
    try {
      const parsed =
        typeof bodyForLog === "string" ? JSON.parse(bodyForLog) : bodyForLog;
      console.log("Request Body:", parsed);
    } catch {
      console.log("Request Body:", bodyForLog);
    }
  }
  console.groupEnd();
};

const logWhatsAppResponse = (response, responseData) => {
  console.group("📥 API Response");
  console.log("Status:", response.status);
  console.log("Status Text:", response.statusText);
  if (response.status === 204) {
    console.log("Response: No Content (204)");
  } else {
    console.log("Response Data:", responseData);
  }
  console.groupEnd();
};

const logWhatsAppNetworkError = (fullUrl, method, bodyForLog, error) => {
  console.group("❌ API Error");
  console.error("Error:", error);
  console.error("Request URL:", fullUrl);
  console.error("Request Method:", method);
  if (bodyForLog !== undefined && bodyForLog !== null && bodyForLog !== "") {
    try {
      const parsed =
        typeof bodyForLog === "string" ? JSON.parse(bodyForLog) : bodyForLog;
      console.error("Request Body:", parsed);
    } catch {
      console.error("Request Body:", bodyForLog);
    }
  }
  console.groupEnd();
};

/**
 * @returns {{ ok: boolean, message?: string }}
 */
export function getWhatsAppApiConfigStatus() {
  const baseUrl = String(API_CONFIG.whatsappApi?.baseUrl || "").trim();
  const apiKey = String(API_CONFIG.whatsappApi?.apiKey || "").trim();
  if (!baseUrl) {
    return {
      ok: false,
      message:
        "WhatsApp messaging is not set up: the service address is missing. Your administrator can enable it in the application configuration.",
    };
  }
  if (!apiKey) {
    return {
      ok: false,
      message:
        "WhatsApp messaging is not set up: the service credentials are missing. Your administrator can enable it in the application configuration.",
    };
  }
  return { ok: true };
}

/**
 * True when SPA can POST to Admin Flask `/whatsapp/message-media/refresh` (same-origin proxy).
 * Media refresh must not call whatsapp_microservice from the browser (CORS).
 * @returns {boolean}
 */
export function adminApiAllowsWhatsAppMediaRefreshProxy() {
  return Boolean(
    String(API_CONFIG.baseUrl || "").trim() &&
      String(API_CONFIG.apiKey || "").trim()
  );
}

async function whatsappJsonFetch(path, options = {}) {
  const { baseUrl, apiKey, useProxy } = API_CONFIG.whatsappApi;
  const url = joinUrl(baseUrl, path);
  const method = (options.method || "GET").toUpperCase();
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(useProxy
      ? { "X-API-Key": apiKey }
      : { "x-api-key": apiKey }),
    ...options.headers,
  };

  logWhatsAppRequest(url, method, headers, options.body);

  let response;
  try {
    response = await fetch(url, { ...options, method, headers });
  } catch (err) {
    logWhatsAppNetworkError(url, method, options.body, err);
    const msg =
      err?.message?.includes("Failed to fetch") || err?.name === "TypeError"
        ? "Cannot reach the WhatsApp service. Check your connection, or try again later. If the problem continues, contact support."
        : err?.message || "Could not reach the WhatsApp service.";
    throw new Error(msg);
  }

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text.slice(0, 500) };
    }
  }

  logWhatsAppResponse(response, data);

  if (!response.ok) {
    const hint = data?.error || data?.message || response.statusText || `HTTP ${response.status}`;
    throw new Error(hint);
  }

  return data;
}

/**
 * Best-effort extraction of template identifiers from n8n `items[0].json` (1msg shape varies).
 * @returns {string[]}
 */
export function extractTemplateNamesFromN8nWrapper(data) {
  const items = data?.items;
  if (!Array.isArray(items) || !items.length) return [];
  const root = items[0]?.json;
  if (!root || typeof root !== "object") return [];

  const names = [];

  const push = (v) => {
    if (typeof v === "string" && v.trim()) names.push(v.trim());
  };

  const scanArray = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const row of arr) {
      if (!row || typeof row !== "object") continue;
      push(row.name);
      push(row.template_name);
      push(row.templateName);
      if (typeof row.id === "string" && row.id.includes("_")) push(row.id);
    }
  };

  scanArray(root.templates);
  scanArray(root.data);
  scanArray(root.elements);
  scanArray(root.message_templates);
  scanArray(root.items);
  scanArray(root.results);

  if (Array.isArray(root)) {
    scanArray(root);
  }

  push(root.template);
  push(root.name);

  return [...new Set(names)].sort((a, b) => a.localeCompare(b));
}

/**
 * Highest placeholder index in WhatsApp template copy (`{{1}}`, `{{2}}`, …).
 * @param {string | undefined | null} s
 * @returns {number}
 */
export function maxPlaceholderIndex(s) {
  if (!s || typeof s !== "string") return 0;
  let max = 0;
  const re = /\{\{(\d+)\}\}/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

/**
 * Template rows from GET /whatsapp/templates (`items[0].json.templates`).
 * @param {unknown} data
 * @returns {object[]}
 */
export function extractTemplatesArrayFromN8nWrapper(data) {
  const items = data?.items;
  if (!Array.isArray(items) || !items.length) return [];
  const root = items[0]?.json;
  if (!root || typeof root !== "object") return [];
  const arr = root.templates;
  return Array.isArray(arr) ? arr : [];
}

/** Normalize Meta / 1msg component `type` (handles `Body`, `limited_time_offer`, spaces). */
function normalizeComponentType(component) {
  return String(component?.type ?? "")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();
}

/**
 * Derive variable slot counts from `OneMsgTemplateDefinition.components` (whatsapp_API/openapi.json).
 *
 * Covers typical WABA templates in the wild: TEXT/IMAGE/DOCUMENT/VIDEO HEADER, BODY/FOOTER {{n}},
 * dynamic URL buttons, and flags CAROUSEL / LIMITED_TIME_OFFER / PRODUCT header as unsupported here.
 * AUTHENTICATION / COPY_CODE flows may still need provider-specific payloads beyond plain `text`.
 *
 * @param {object | null | undefined} template
 */
export function analyzeWhatsAppTemplateDefinition(template) {
  const components = Array.isArray(template?.components) ? template.components : [];
  let headerSlots = 0;
  let headerPreview = "";
  /** IMAGE / DOCUMENT / VIDEO header — requires media link in send payload (no {{n}} in component text). */
  let headerMediaFormat = null;
  let bodySlots = 0;
  let bodyPreview = "";
  let footerSlots = 0;
  let footerPreview = "";
  /** UI cannot build params yet (carousel, product header, …). */
  let unsupportedReason = null;
  /** @type {{ index: number, type?: string, label: string, slots: number, previewUrl?: string }[]} */
  const buttonGroups = [];
  /** Flatten all rows from every BUTTONS component (indices match provider send order). */
  const flatButtons = [];

  for (const c of components) {
    const compType = normalizeComponentType(c);
    if (compType === "CAROUSEL" || compType === "LIMITED_TIME_OFFER") {
      unsupportedReason = unsupportedReason || "carousel_or_limited_offer";
      continue;
    }
    if (compType === "HEADER") {
      const hasText = typeof c.text === "string" && c.text.length > 0;
      const fmtRaw =
        c.format != null && String(c.format).trim() !== ""
          ? String(c.format).trim().toUpperCase()
          : hasText
            ? "TEXT"
            : "";

      if ((fmtRaw === "TEXT" || (!c.format && hasText)) && hasText) {
        const n = maxPlaceholderIndex(c.text);
        if (n > headerSlots) headerSlots = n;
        headerPreview = c.text;
      } else if (fmtRaw === "IMAGE" || fmtRaw === "DOCUMENT" || fmtRaw === "VIDEO") {
        headerMediaFormat = fmtRaw;
        headerPreview =
          fmtRaw === "IMAGE"
            ? "(IMAGE header — provide an HTTPS link to the image)"
            : fmtRaw === "VIDEO"
              ? "(VIDEO header — provide an HTTPS link to the video)"
              : "(DOCUMENT header — provide an HTTPS link to the document)";
      } else if (fmtRaw === "PRODUCT") {
        unsupportedReason = unsupportedReason || "product_header";
      }
    }
    if (compType === "BODY" && c.text) {
      const n = maxPlaceholderIndex(c.text);
      if (n >= bodySlots) {
        bodySlots = n;
        bodyPreview = c.text;
      }
    }
    if (compType === "FOOTER" && c.text) {
      const n = maxPlaceholderIndex(c.text);
      if (n >= footerSlots) {
        footerSlots = n;
        footerPreview = c.text;
      }
    }
    if (compType === "BUTTONS" && Array.isArray(c.buttons)) {
      for (const btn of c.buttons) flatButtons.push(btn);
    }
  }

  flatButtons.forEach((btn, idx) => {
    const url = btn?.url;
    if (typeof url === "string" && /\{\{\d+\}\}/.test(url)) {
      const slots = maxPlaceholderIndex(url);
      if (slots > 0) {
        buttonGroups.push({
          index: idx,
          type: btn?.type,
          label: String(btn?.text || "").trim() || `Button ${idx + 1}`,
          slots,
          previewUrl: url,
        });
      }
    }
  });

  return {
    name: template?.name,
    language: template?.language,
    namespace: template?.namespace,
    category: template?.category,
    headerSlots,
    headerPreview,
    headerMediaFormat,
    bodySlots,
    bodyPreview,
    footerSlots,
    footerPreview,
    buttonGroups,
    unsupportedReason,
  };
}

/**
 * Build `params` for POST /whatsapp/send-template from UI values (ordered `{{1}}`… within each section).
 * @param {ReturnType<typeof analyzeWhatsAppTemplateDefinition>} analysis
 * @param {{
 *   headerValues: string[],
 *   bodyValues: string[],
 *   footerValues: string[],
 *   buttonValuesByIndex: Record<number, string[]>,
 *   headerMediaUrl?: string,
 *   headerDocumentFilename?: string,
 * }} values
 * @returns {object[]}
 */
export function buildWhatsAppTemplateParams(analysis, values) {
  const params = [];
  const {
    headerValues,
    bodyValues,
    footerValues,
    buttonValuesByIndex,
    headerMediaUrl,
    headerDocumentFilename,
  } = values;

  const mediaLink = String(headerMediaUrl ?? "").trim();
  const fmt = analysis.headerMediaFormat;

  if (fmt === "IMAGE" && mediaLink) {
    params.push({
      type: "header",
      parameters: [{ type: "image", image: { link: mediaLink } }],
    });
  } else if (fmt === "VIDEO" && mediaLink) {
    params.push({
      type: "header",
      parameters: [{ type: "video", video: { link: mediaLink } }],
    });
  } else if (fmt === "DOCUMENT" && mediaLink) {
    params.push({
      type: "header",
      parameters: [
        {
          type: "document",
          document: {
            link: mediaLink,
            filename: String(headerDocumentFilename ?? "").trim() || "document.pdf",
          },
        },
      ],
    });
  }

  if (analysis.headerSlots > 0) {
    params.push({
      type: "header",
      parameters: headerValues.slice(0, analysis.headerSlots).map((text) => ({
        type: "text",
        text: String(text ?? "").trim(),
      })),
    });
  }
  if (analysis.bodySlots > 0) {
    params.push({
      type: "body",
      parameters: bodyValues.slice(0, analysis.bodySlots).map((text) => ({
        type: "text",
        text: String(text ?? "").trim(),
      })),
    });
  }
  if (analysis.footerSlots > 0) {
    params.push({
      type: "footer",
      parameters: footerValues.slice(0, analysis.footerSlots).map((text) => ({
        type: "text",
        text: String(text ?? "").trim(),
      })),
    });
  }

  const sortedButtons = [...analysis.buttonGroups].sort((a, b) => a.index - b.index);
  for (const bg of sortedButtons) {
    const arr = buttonValuesByIndex[bg.index] || [];
    params.push({
      type: "button",
      sub_type: "url",
      index: String(bg.index),
      parameters: arr.slice(0, bg.slots).map((text) => ({
        type: "text",
        text: String(text ?? "").trim(),
      })),
    });
  }

  return params;
}

/**
 * Templates with no `{{n}}` placeholders (fixed approved copy only).
 * Sent as a single body component with empty parameters so `params` stays a non-empty array
 * for APIs that require it (see POST /whatsapp/send-template in whatsapp_API/openapi.json).
 */
export const STATIC_WHATSAPP_TEMPLATE_PARAMS = [{ type: "body", parameters: [] }];

/** WhatsApp / 1msg rendered BODY limit (static template + substituted {{n}} values). */
export const WHATSAPP_TEMPLATE_BODY_MAX_LENGTH = 1024;

/**
 * Replace `{{1}}`, `{{2}}`, … with `parameterValues[n-1]`.
 * @param {string | null | undefined} templateText
 * @param {string[]} parameterValues
 */
export function renderTemplateSectionWithPlaceholders(templateText, parameterValues) {
  const text = String(templateText ?? "");
  if (!text) return "";
  return text.replace(/\{\{(\d+)\}\}/g, (_match, numStr) => {
    const n = parseInt(numStr, 10);
    if (!Number.isFinite(n) || n < 1) return "";
    const raw = parameterValues[n - 1];
    return raw == null ? "" : String(raw);
  });
}

/**
 * Rendered BODY character count for WhatsApp send-template validation.
 * @param {ReturnType<typeof analyzeWhatsAppTemplateDefinition>} analysis
 * @param {string[]} bodyValues
 */
export function measureRenderedTemplateBodyLength(analysis, bodyValues) {
  if (!analysis?.bodyPreview || !(analysis.bodySlots > 0)) return 0;
  const rendered = renderTemplateSectionWithPlaceholders(
    analysis.bodyPreview,
    Array.isArray(bodyValues) ? bodyValues : []
  );
  return rendered.length;
}

/**
 * @param {ReturnType<typeof analyzeWhatsAppTemplateDefinition>} analysis
 * @param {string[]} bodyValues
 * @param {object[]} [recipients]
 * @param {(analysis: object, bodyValues: string[], recipient: object) => string[]} [resolveBodyValuesForRecipient]
 */
export function assessTemplateBodyLength(
  analysis,
  bodyValues,
  { recipients, resolveBodyValuesForRecipient } = {}
) {
  const max = WHATSAPP_TEMPLATE_BODY_MAX_LENGTH;
  if (!analysis || analysis.bodySlots <= 0) {
    return { ok: true, length: 0, max, worstRecipient: null };
  }

  if (
    Array.isArray(recipients) &&
    recipients.length > 0 &&
    typeof resolveBodyValuesForRecipient === "function"
  ) {
    let worstLen = 0;
    let worstRecipient = null;
    for (const recipient of recipients) {
      const resolved = resolveBodyValuesForRecipient(analysis, bodyValues, recipient);
      const len = measureRenderedTemplateBodyLength(analysis, resolved);
      if (len > worstLen) {
        worstLen = len;
        worstRecipient = recipient;
      }
    }
    return { ok: worstLen <= max, length: worstLen, max, worstRecipient };
  }

  const length = measureRenderedTemplateBodyLength(analysis, bodyValues);
  return { ok: length <= max, length, max, worstRecipient: null };
}

/**
 * @param {{ ok: boolean, length: number, max: number, worstRecipient?: object | null }} assessment
 */
export function templateBodyLengthErrorMessage(assessment) {
  if (!assessment || assessment.ok) return "";
  const who = assessment.worstRecipient
    ? ` (longest for ${String(
        assessment.worstRecipient.contact_name ||
          assessment.worstRecipient.profile_name ||
          assessment.worstRecipient.customer_code ||
          "a contact"
      ).trim()})`
    : "";
  return `Message body is too long (${assessment.length} / ${assessment.max} characters). WhatsApp limits template body text to ${assessment.max} characters including the approved template wording and your field values${who}. Shorten the message fields.`;
}

/**
 * @param {{ ok: boolean, length: number, max: number }} assessment
 */
export function templateBodyLengthHelperText(assessment) {
  if (!assessment || assessment.length <= 0) return "";
  const over = !assessment.ok;
  return `Rendered body: ${assessment.length} / ${assessment.max} characters${over ? " — over limit" : ""}`;
}

/**
 * @returns {Promise<{ names: string[], raw: unknown }>}
 */
export async function fetchWhatsAppTemplates() {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const data = await whatsappJsonFetch("/whatsapp/templates", { method: "GET" });
  const names = extractTemplateNamesFromN8nWrapper(data);
  return { names, raw: data };
}

/**
 * @returns {Promise<unknown>}
 */
export async function fetchWhatsAppStatus() {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  return whatsappJsonFetch("/whatsapp/status", { method: "GET" });
}

/**
 * `GET /messages/inbox/unread-count` — agent inbox unread total (`whatsapp_read`, messages with `wa_id`).
 * @param {{ waIds?: string[] }} [options]
 * @returns {Promise<number>}
 */
export async function fetchWhatsAppInboxUnreadCount(options = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const waIds = Array.isArray(options.waIds)
    ? [...new Set(options.waIds.map((id) => normalizeWaId(id)).filter(Boolean))]
    : [];
  const query = new URLSearchParams();
  if (waIds.length > 0) {
    query.set("wa_ids", waIds.join(","));
  }
  const path = query.toString()
    ? `/messages/inbox/unread-count?${query.toString()}`
    : "/messages/inbox/unread-count";

  const body = await whatsappJsonFetch(path, { method: "GET" });
  const total = Number(body?.total_unread_messages);
  return Number.isFinite(total) && total >= 0 ? total : 0;
}

const DASHBOARD_UNREAD_MIN_INTERVAL_MS = 60_000;
let dashboardUnreadCache = null;
let dashboardUnreadFetchedAt = 0;
let dashboardUnreadInFlight = null;

/**
 * Throttled unread count for dashboard widgets (avoids duplicate calls on remount / strict mode).
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<number>}
 */
export async function fetchWhatsAppInboxUnreadCountForDashboard(options = {}) {
  const now = Date.now();
  const force = options.force === true;
  if (
    !force &&
    dashboardUnreadCache !== null &&
    now - dashboardUnreadFetchedAt < DASHBOARD_UNREAD_MIN_INTERVAL_MS
  ) {
    return dashboardUnreadCache;
  }
  if (dashboardUnreadInFlight) {
    return dashboardUnreadInFlight;
  }
  dashboardUnreadInFlight = fetchWhatsAppInboxUnreadCount()
    .then((count) => {
      dashboardUnreadCache = count;
      dashboardUnreadFetchedAt = Date.now();
      return count;
    })
    .finally(() => {
      dashboardUnreadInFlight = null;
    });
  return dashboardUnreadInFlight;
}

/**
 * Business display name from `GET /whatsapp/status` (1msg instance), when present.
 * @param {unknown} statusPayload
 * @returns {string}
 */
export function extractBusinessProfileNameFromStatus(statusPayload) {
  const j = statusPayload?.items?.[0]?.json;
  if (!j || typeof j !== "object") return "";

  const candidates = [
    j.pushname,
    j.profileName,
    j.profile_name,
    j.businessName,
    j.business_name,
    j.displayName,
    j.name,
  ];

  for (const value of candidates) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed || looksLikeRawWaId(trimmed)) continue;
    return trimmed;
  }

  return "";
}

/**
 * Resolved business profile label for outbound message previews.
 * @param {unknown} [statusPayload]
 * @returns {string}
 */
export function resolveBusinessProfileName(statusPayload) {
  const fromStatus = extractBusinessProfileNameFromStatus(statusPayload);
  if (fromStatus) return fromStatus;
  const configured = String(API_CONFIG.whatsappApi?.businessProfileName || "").trim();
  return configured || "NanoTech NetComs";
}

/**
 * Strip WhatsApp JID suffixes (`27135180008@c.us`) and normalize to 11-digit ZA MSISDN when possible.
 * @param {string} waId
 * @returns {string}
 */
export function normalizeWaId(waId) {
  if (waId == null || waId === "") return "";
  let local = String(waId).trim();
  const at = local.indexOf("@");
  if (at > 0) local = local.slice(0, at);
  const formatted = formatPhoneNumber(local);
  if (formatted) return formatted;
  const digits = local.replace(/\D/g, "");
  return digits;
}

/**
 * Profile names that are really JIDs / numbers should not be used as display names.
 * @param {string | null | undefined} value
 * @returns {boolean}
 */
export function looksLikeRawWaId(value) {
  if (value == null || value === "") return false;
  const s = String(value).trim();
  if (/@c\.us|@s\.whatsapp|@g\.us/i.test(s)) return true;
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 10 && !/\s/.test(s) && !/\[.*\]/.test(s)) {
    const letters = s.replace(/[^a-zA-Z]/g, "");
    if (letters.length < 3) return true;
  }
  return false;
}

/**
 * @param {string} waId
 * @returns {boolean}
 */
export function isHelpdeskWaId(waId) {
  const configured = normalizeWaId(API_CONFIG.whatsappApi?.helpdeskWaId || "");
  if (!configured) return false;
  return normalizeWaId(waId) === configured;
}

/**
 * Phone variants used to match `customers.telephone1` / `telephone2` / `fax` to `wa_id`.
 * @param {string} waId
 * @returns {string[]}
 */
export function phoneLookupVariantsForWaId(waId) {
  const id = normalizeWaId(waId).replace(/\D/g, "");
  if (!id) return [];
  const variants = new Set([id, `+${id}`]);
  if (id.startsWith("27") && id.length > 2) {
    variants.add(`0${id.slice(2)}`);
  }
  return [...variants];
}

/**
 * @param {unknown} v
 * @returns {number | null}
 */
function pickNumericCoordinate(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Reads coordinates from `GET /messages/chat` **`attachment.location`** (or a bare location object).
 * @param {object | null | undefined} source — `MessagesStructuredAttachment` or `{ latitude, longitude, … }`
 * @returns {{ lat: number, lng: number, name?: string | null, address?: string | null } | null}
 */
export function extractWhatsAppLocationCoordinates(source) {
  if (!source || typeof source !== "object") return null;
  const loc = source.location && typeof source.location === "object" ? source.location : source;
  if (!loc || typeof loc !== "object") return null;
  const lat =
    pickNumericCoordinate(loc.latitude) ??
    pickNumericCoordinate(loc.lat) ??
    pickNumericCoordinate(loc.Latitude);
  const lng =
    pickNumericCoordinate(loc.longitude) ??
    pickNumericCoordinate(loc.lng) ??
    pickNumericCoordinate(loc.lon) ??
    pickNumericCoordinate(loc.Longitude);
  if (lat == null || lng == null) return null;
  const name = loc.name != null ? String(loc.name) : loc.title != null ? String(loc.title) : null;
  const address = loc.address != null ? String(loc.address) : null;
  return { lat, lng, name, address };
}

/**
 * One-line summary for inbox / quoted from **`MessagesStructuredAttachment.contacts`**.
 * @param {unknown[]} contacts
 * @returns {string}
 */
export function formatWhatsAppContactsSummary(contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) return "";
  const parts = [];
  const slice = contacts.slice(0, 4);
  for (const c of slice) {
    const rawName = c?.name;
    const name =
      rawName?.formatted_name ||
      rawName?.formattedName ||
      [rawName?.first_name, rawName?.last_name].filter(Boolean).join(" ").trim() ||
      (typeof rawName === "string" ? rawName : null) ||
      c?.displayName ||
      "Contact";
    parts.push(String(name).trim());
  }
  const suffix = contacts.length > slice.length ? ` +${contacts.length - slice.length}` : "";
  return `${parts.join(", ")}${suffix}`;
}

/**
 * Short label for **`attachment`** (inbox preview, quoted line, fallbacks).
 * @param {object | null | undefined} attachment — `MessagesStructuredAttachment`
 * @returns {string}
 */
export function formatWhatsAppStructuredAttachmentPreview(attachment) {
  if (!attachment || typeof attachment !== "object") return "";
  const mt = String(attachment.message_type || "").toLowerCase();

  if (attachment.location || mt === "location") {
    const coords = extractWhatsAppLocationCoordinates(attachment);
    if (coords?.name) {
      return `Location: ${coords.name}`;
    }
    if (coords) {
      return `Location: ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
    }
    return "Location";
  }

  const list = attachment.contacts;
  if ((Array.isArray(list) && list.length > 0) || mt === "contacts") {
    const summary = formatWhatsAppContactsSummary(Array.isArray(list) ? list : []);
    return summary ? `Contact: ${summary}` : "Contact";
  }

  if (mt === "interactive") return "Interactive message";
  if (["button_reply", "list_reply"].includes(mt)) return "Interactive reply";
  return "";
}

/**
 * Resolves map coordinates for a chat row: **`attachment.location`**, then legacy **`message_text`** patterns.
 * @param {object | null | undefined} message — `MessagesChatItem` shape
 * @returns {{ lat: number, lng: number, name?: string | null, address?: string | null } | null}
 */
export function resolveWhatsAppMessageLocation(message) {
  if (!message) return null;
  const fromAtt = extractWhatsAppLocationCoordinates(message.attachment);
  if (fromAtt) return fromAtt;

  const mt = String(message.message_type || "").toLowerCase();
  if (mt !== "location") return null;
  const text = message.message_text || "";
  const locationRegex = /Location:\s*(-?\d+\.\d+),\s*(-?\d+\.\d+)/i;
  const m = text.match(locationRegex);
  if (m) {
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng, name: null, address: null };
  }
  const coords = text.match(/(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
  if (coords) {
    const lat = Number(coords[1]);
    const lng = Number(coords[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng, name: null, address: null };
  }
  return null;
}

/**
 * Display parts for one vCard-style object under **`attachment.contacts`**.
 * @param {object} contact
 * @returns {{ name: string, phones: string[] }}
 */
export function getWhatsAppContactDisplayParts(contact) {
  const rawName = contact?.name;
  const name =
    rawName?.formatted_name ||
    rawName?.formattedName ||
    [rawName?.first_name, rawName?.last_name].filter(Boolean).join(" ").trim() ||
    (typeof rawName === "string" ? rawName : null) ||
    contact?.displayName ||
    "Contact";
  const phones = [];
  const rawPhones = contact?.phones ?? contact?.phoneNumbers ?? [];
  if (Array.isArray(rawPhones)) {
    for (const p of rawPhones) {
      const num = p?.phone ?? p?.number ?? (typeof p === "string" ? p : null);
      if (num != null && String(num).trim() !== "") phones.push(String(num).trim());
    }
  }
  return { name: String(name).trim(), phones };
}

/**
 * Plain-text preview for a quoted message (chat bubble or inbox).
 * @param {object | null | undefined} message
 * @param {number} [maxLen]
 * @returns {string}
 */
export function quotedMessagePreviewLine(message, maxLen = 120) {
  if (!message) return "";
  const t = (message.message_text || "").trim();
  if (t.length > maxLen) {
    return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
  }
  if (t) return t;
  const fromAtt = formatWhatsAppStructuredAttachmentPreview(message.attachment);
  if (fromAtt) {
    return fromAtt.length > maxLen
      ? `${fromAtt.slice(0, Math.max(0, maxLen - 1))}…`
      : fromAtt;
  }
  const mt = String(message.message_type || "").toLowerCase();
  if (["image", "video", "audio", "document", "sticker"].includes(mt)) {
    return mt.charAt(0).toUpperCase() + mt.slice(1);
  }
  if (mt === "location") return "Location";
  if (mt === "contacts") return "Contact";
  if (mt === "template") return "Template message";
  return "(No content)";
}

/**
 * Build `data:<mime>;base64,...` when chat API returned inline attachment bytes (≤ inline cap).
 * @param {object | null | undefined} media
 * @returns {string | null}
 */
export function buildWhatsAppInlineDataUrl(media) {
  if (!media || media.inline_data_omitted) return null;
  const mime = media.mime_type;
  const raw = media.data_base64;
  if (!mime || raw == null || raw === "") return null;
  const b64 = String(raw).replace(/[\s\r\n]+/g, "");
  if (!b64) return null;
  return `data:${mime};base64,${b64}`;
}

/**
 * `POST /messages/{message_id}/media/refresh` on whatsapp_microservice, **via Admin Flask**
 * (`POST /whatsapp/message-media/refresh`) so the browser does not cross-call the microservice (CORS).
 * @param {string} messageId
 * @param {{ url?: string | null }} [options]
 * @returns {Promise<unknown>}
 */
export async function refreshWhatsAppMessageMedia(messageId, options = {}) {
  if (!messageId) throw new Error("Message ID is required");
  if (!adminApiAllowsWhatsAppMediaRefreshProxy()) {
    throw new Error(
      "Admin API is not configured (base URL / API key). Media refresh is proxied server-side and requires the same-origin Admin API client."
    );
  }

  const payload = { message_id: messageId };
  if (typeof options.url === "string" && options.url.trim()) {
    payload.url = options.url.trim();
  }

  return api.post("/whatsapp/message-media/refresh", payload);
}

/**
 * @param {unknown} item
 * @returns {string}
 */
function inboxPreviewText(item) {
  const text = item?.latest_message_text;
  if (text != null && String(text).trim() !== "") {
    return String(text);
  }
  const fromAtt = formatWhatsAppStructuredAttachmentPreview(item?.attachment);
  if (fromAtt) return fromAtt;
  const type = String(item?.message_type || "").toLowerCase();
  if (type && type !== "text" && type !== "chat") {
    return "(Media message)";
  }
  if (item?.media_id) {
    return "(Media message)";
  }
  return "";
}

/**
 * Map `GET /messages/inbox` item to the shape consumed by whatsapp-inbox UI.
 * @param {object} item
 * @returns {object}
 */
export function mapMessagesInboxItem(item) {
  const waId = normalizeWaId(item?.wa_id || "");
  const profileName = item?.profile_name ?? null;
  return {
    wa_id: waId,
    profile_name:
      profileName && !looksLikeRawWaId(profileName) ? String(profileName).trim() : null,
    latest_message_text: inboxPreviewText(item),
    latest_message: item?.latest_message ?? null,
    latest_message_direction: item?.latest_message_direction ?? "",
    has_unread: Boolean(item?.has_unread),
    unread_count: Number(item?.unread_count) || 0,
    oldest_unread: item?.oldest_unread ?? null,
    message_id: item?.message_id,
    message_type: item?.message_type ?? null,
    media_id: item?.media_id ?? null,
    media_file_name: item?.media_file_name ?? null,
    quoted_message_id: item?.quoted_message_id ?? null,
    customer_name: item?.customer_name ?? null,
    has_multiple_customers: Boolean(item?.has_multiple_customers),
    customers: Array.isArray(item?.customers) ? item.customers : [],
    attachment: item?.attachment ?? null,
  };
}

/**
 * Unread first (oldest unread asc), then by latest activity desc — matches legacy inbox UX.
 * @param {object[]} contacts
 */
export function sortInboxContacts(contacts) {
  const list = [...contacts];
  list.sort((a, b) => {
    if (a.has_unread && !b.has_unread) return -1;
    if (!a.has_unread && b.has_unread) return 1;
    if (a.has_unread && b.has_unread) {
      const ao = a.oldest_unread ? new Date(a.oldest_unread).getTime() : 0;
      const bo = b.oldest_unread ? new Date(b.oldest_unread).getTime() : 0;
      if (ao !== bo) return ao - bo;
    }
    const at = a.latest_message ? new Date(a.latest_message).getTime() : 0;
    const bt = b.latest_message ? new Date(b.latest_message).getTime() : 0;
    return bt - at;
  });
  return list;
}

/**
 * `GET /messages/inbox` — fetches all pages up to `maxPages` (default: until exhausted).
 * @param {{ limit?: number, maxPages?: number, waIds?: string[] }} [options]
 * @returns {Promise<{ data: object[], pagination: object | null, totalUnreadMessages: number }>}
 */
export async function fetchWhatsAppInbox(options = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const limit = Math.min(Math.max(Number(options.limit) || 500, 1), 500);
  const maxPages = Number(options.maxPages) || 50;
  const waIds = Array.isArray(options.waIds)
    ? [...new Set(options.waIds.map((id) => normalizeWaId(id)).filter(Boolean))]
    : [];
  const aggregated = [];
  let pagination = null;
  let totalUnreadMessages = 0;
  let page = 1;

  while (page <= maxPages) {
    const query = new URLSearchParams({
      limit: String(limit),
      page: String(page),
    });
    if (waIds.length > 0) {
      query.set("wa_ids", waIds.join(","));
    }
    const body = await whatsappJsonFetch(`/messages/inbox?${query.toString()}`, {
      method: "GET",
    });
    if (page === 1) {
      const unreadTotal = Number(body?.total_unread_messages);
      totalUnreadMessages =
        Number.isFinite(unreadTotal) && unreadTotal >= 0 ? unreadTotal : 0;
    }
    const batch = Array.isArray(body?.data)
      ? body.data.map(mapMessagesInboxItem).filter((row) => row.wa_id && !isHelpdeskWaId(row.wa_id))
      : [];
    aggregated.push(...batch);
    pagination = body?.pagination ?? pagination;
    const totalPages = pagination?.pages ?? 1;
    if (page >= totalPages || batch.length === 0) {
      break;
    }
    page += 1;
  }

  return {
    data: sortInboxContacts(aggregated),
    pagination,
    totalUnreadMessages,
  };
}

/**
 * Map `GET /messages/chat/{wa_id}` item for the chat UI.
 * @param {object} item
 * @returns {object}
 */
export function mapMessagesChatItem(item) {
  const timestamp = item?.message_timestamp ?? item?.message_datetime ?? null;
  return {
    message_id: item?.message_id,
    wa_id: item?.wa_id,
    profile_name: item?.profile_name ?? null,
    message_text: item?.message_text ?? null,
    message_datetime: timestamp,
    message_type: item?.message_type ?? null,
    direction: item?.direction ?? null,
    message_status: item?.message_status ?? null,
    media_id: item?.media_id ?? null,
    processing_error: item?.processing_error ?? null,
    media:
      item?.media && typeof item.media === "object" && item.media !== null ? item.media : null,
    template_name: item?.template_name ?? null,
    read_status: item?.read_status ?? "unread",
    read_datetime: item?.read_datetime ?? null,
    has_unread: Boolean(item?.has_unread),
    user: item?.viewer ?? null,
    quoted_message_id: item?.quoted_message_id ?? null,
    attachment: item?.attachment ?? null,
  };
}

/** Helpdesk chat: messages per UI page (matches `GET /messages/chat` `limit`). */
export const WHATSAPP_CHAT_MESSAGES_PER_PAGE = 200;

/** Initial load window: only messages newer than this many days unless "Load older" is used. */
export const WHATSAPP_CHAT_RECENT_DAYS = 30;

/**
 * @param {object | null | undefined} message
 * @returns {number}
 */
export function parseWhatsAppMessageTimestampMs(message) {
  const raw = message?.message_timestamp ?? message?.message_datetime ?? null;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Fetch all messages in the current scope (recent window or full history), ascending.
 * Uses `GET /messages/chat/{wa_id}` pages (`order=desc`) until the scope boundary is reached.
 * @param {string} waId
 * @param {{ include_older?: boolean, recent_days?: number, max_api_pages?: number }} [options]
 */
export async function collectWhatsAppChatScopeMessages(waId, options = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const includeOlder = Boolean(options.include_older);
  const recentDays = Math.max(1, Number(options.recent_days) || WHATSAPP_CHAT_RECENT_DAYS);
  const sinceMs = Date.now() - recentDays * 24 * 60 * 60 * 1000;
  const maxApiPages = Math.min(
    Math.max(Number(options.max_api_pages) || (includeOlder ? 100 : 50), 1),
    100
  );

  const byId = new Map();
  let unreadCount = 0;
  let apiPage = 1;
  let apiTotalPages = 1;
  let hasOlderAvailable = false;

  while (apiPage <= maxApiPages && apiPage <= apiTotalPages) {
    const { messages: batch, pagination, unread_count } = await fetchWhatsAppChatPage(
      normalizedWaId,
      {
        page: apiPage,
        limit: WHATSAPP_CHAT_MESSAGES_PER_PAGE,
        order: "desc",
      }
    );

    if (apiPage === 1) {
      unreadCount = Number(unread_count) || 0;
    }
    if (!batch.length) {
      break;
    }

    apiTotalPages = pagination?.pages ?? 1;

    for (const msg of batch) {
      const ts = parseWhatsAppMessageTimestampMs(msg);
      if (!includeOlder && ts < sinceMs) {
        hasOlderAvailable = true;
        continue;
      }
      const key = msg?.message_id || `${ts}-${msg?.direction || "x"}`;
      byId.set(key, msg);
    }

    const oldestInBatch = parseWhatsAppMessageTimestampMs(batch[batch.length - 1]);
    if (!includeOlder && oldestInBatch < sinceMs) {
      break;
    }

    if (batch.length < WHATSAPP_CHAT_MESSAGES_PER_PAGE) {
      break;
    }
    apiPage += 1;
  }

  if (!includeOlder && apiPage < apiTotalPages) {
    hasOlderAvailable = true;
  }

  const messages = [...byId.values()].sort(
    (a, b) => parseWhatsAppMessageTimestampMs(a) - parseWhatsAppMessageTimestampMs(b)
  );

  return {
    messages,
    unread_count: unreadCount,
    has_older_available: hasOlderAvailable,
    wa_id: normalizedWaId,
  };
}

/**
 * Helpdesk thread view: recent window (or full history), 200 messages per UI page, newest page first.
 * @param {string} waId
 * @param {{ page?: number, include_older?: boolean, recent_days?: number }} [options]
 */
export async function fetchWhatsAppChatThreadView(waId, options = {}) {
  const uiPage = Math.max(Number(options.page) || 1, 1);
  const includeOlder = Boolean(options.include_older);

  const scope = await collectWhatsAppChatScopeMessages(waId, {
    include_older: includeOlder,
    recent_days: options.recent_days,
  });

  const pageSize = WHATSAPP_CHAT_MESSAGES_PER_PAGE;
  const totalInScope = scope.messages.length;
  const totalPages = Math.max(1, Math.ceil(totalInScope / pageSize));
  const safePage = Math.min(uiPage, totalPages);
  const end = totalInScope - (safePage - 1) * pageSize;
  const start = Math.max(0, end - pageSize);
  const pageMessages = scope.messages.slice(start, end);

  return {
    messages: pageMessages,
    unread_count: scope.unread_count,
    wa_id: scope.wa_id,
    page: safePage,
    pages: totalPages,
    total: totalInScope,
    show_pagination: totalInScope > pageSize,
    show_load_older: !includeOlder && scope.has_older_available,
    include_older: includeOlder,
    recent_days: options.recent_days ?? WHATSAPP_CHAT_RECENT_DAYS,
  };
}

/**
 * `GET /messages/chat/{wa_id}` — thread messages for one contact.
 * @param {string} waId
 * @param {{ limit?: number, fetch_all?: boolean, order?: 'asc' | 'desc' }} [options]
 * @returns {Promise<{ messages: object[], total: number, page: number, pages: number, unread_count: number, is_limited: boolean, error?: string }>}
 */
export async function fetchWhatsAppChat(waId, options = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const limit = Math.min(Math.max(Number(options.limit) || 200, 1), 500);
  const order = options.order === "desc" ? "desc" : "asc";
  const fetchAll = Boolean(options.fetch_all);
  const maxPages = fetchAll ? 50 : 1;
  const aggregated = [];
  let pagination = null;
  let unreadCount = 0;
  let page = 1;

  while (page <= maxPages) {
    const query = new URLSearchParams({
      limit: String(limit),
      page: String(page),
      order,
    });
    const body = await whatsappJsonFetch(
      `/messages/chat/${encodeURIComponent(normalizedWaId)}?${query.toString()}`,
      { method: "GET" }
    );

    if (page === 1) {
      const unreadTotal = Number(body?.unread_count);
      unreadCount = Number.isFinite(unreadTotal) && unreadTotal >= 0 ? unreadTotal : 0;
    }

    const batch = Array.isArray(body?.data) ? body.data.map(mapMessagesChatItem) : [];
    aggregated.push(...batch);
    pagination = body?.pagination ?? pagination;

    const totalPages = pagination?.pages ?? 1;
    if (!fetchAll || page >= totalPages || batch.length === 0) {
      break;
    }
    page += 1;
  }

  const total = pagination?.total ?? aggregated.length;
  const pages = pagination?.pages ?? 1;

  return {
    messages: aggregated,
    total,
    page: 1,
    pages,
    unread_count: unreadCount,
    is_limited: !fetchAll && pages > 1,
  };
}

/**
 * Single page from `GET /messages/chat/{wa_id}`.
 * @param {string} waId
 * @param {{ page?: number, limit?: number, order?: 'asc' | 'desc' }} [options]
 */
export async function fetchWhatsAppChatPage(waId, options = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const limit = Math.min(Math.max(Number(options.limit) || 200, 1), 500);
  const page = Math.max(Number(options.page) || 1, 1);
  const order = options.order === "asc" ? "asc" : "desc";

  const query = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    order,
  });
  const body = await whatsappJsonFetch(
    `/messages/chat/${encodeURIComponent(normalizedWaId)}?${query.toString()}`,
    { method: "GET" }
  );

  return {
    messages: Array.isArray(body?.data) ? body.data.map(mapMessagesChatItem) : [],
    unread_count: Number(body?.unread_count) || 0,
    pagination: body?.pagination ?? null,
  };
}

/** Max pages scanned when locating a bubble by `message_id` (covers large threads slowly). */
const FIND_WHATSAPP_MESSAGE_MAX_PAGES = 48;

/**
 * Locate a chat row returned by whatsapp_microservice (paged `/messages/chat/{wa_id}` DESC).
 * @param {string} waId
 * @param {string} messageId
 * @param {{ maxPages?: number }} [opts]
 */
export async function findWhatsAppChatMessageById(waId, messageId, opts = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const norm = normalizeWaId(waId);
  if (!norm || !messageId) return null;

  const maxPages = Math.min(
    Math.max(Number(opts.maxPages) || FIND_WHATSAPP_MESSAGE_MAX_PAGES, 1),
    100
  );

  for (let page = 1; page <= maxPages; page += 1) {
    const { messages, pagination } = await fetchWhatsAppChatPage(norm, {
      page,
      limit: 500,
      order: "desc",
    });
    const hit = messages.find((m) => m.message_id === messageId);
    if (hit) return hit;

    const totalPages = Number(pagination?.pages) || page;
    if (page >= totalPages) break;
  }
  return null;
}

/**
 * Resolve a data URL for preview/download from whatsapp_microservice chat payloads (`message.media`).
 * Does not call the legacy Admin Flask `/whatsapp_webhook/show_media`.
 *
 * @param {string | undefined | null} waId — digits wa id / route param peer
 * @param {{ message_id?: string, wa_id?: string|null, media?: object|null }} message — thread bubble row shape
 * @param {{ bypassLocal?: boolean }} [options] — after server refresh, set true so we re-scan chat instead of stale inline
 */
export async function resolveWhatsAppMessageMediaPresentation(waId, message, options = {}) {
  const bypassLocal = Boolean(options?.bypassLocal);

  if (!bypassLocal) {
    const fromRow = buildWhatsAppInlineDataUrl(message?.media);
    if (fromRow) {
      return {
        mediaUrl: fromRow,
        caption: message?.media?.caption ?? null,
        mimeType: message?.media?.mime_type ?? null,
        mediaDataExists: true,
      };
    }
  }

  const norm = normalizeWaId(waId || message?.wa_id || "");
  const mid = message?.message_id;
  if (!norm || !mid) {
    throw new Error("Missing conversation id (`wa_id`) or `message_id` for WhatsApp API media lookup.");
  }

  const remote = await findWhatsAppChatMessageById(norm, mid);
  const mediaObj = remote?.media || message?.media;

  const url = buildWhatsAppInlineDataUrl(mediaObj);
  if (url) {
    return {
      mediaUrl: url,
      caption: mediaObj?.caption ?? null,
      mimeType: mediaObj?.mime_type ?? null,
      mediaDataExists: true,
    };
  }

  if (mediaObj?.inline_data_omitted) {
    throw new Error(
      "This attachment exceeds the WhatsApp API inline preview limit (~1 MiB). Use refresh so the blob is synced, then try again—or open from the original device/chat."
    );
  }

  throw new Error(
    "WhatsApp API did not include inline attachment bytes yet. Tap refresh after the media service finishes downloading."
  );
}

/**
 * `POST /messages/{message_id}/read` — mark one inbound message read + send read receipt.
 * Sends **`viewer`** as the logged-in username (`sec_users.login`) when available.
 * @param {string} messageId
 * @param {{ viewer?: string | null }} [options]
 */
export async function markWhatsAppMessageRead(messageId, options = {}) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);
  if (!messageId) throw new Error("Message ID is required");

  const viewer = String(options.viewer ?? getStoredUserLogin() ?? "")
    .trim()
    .slice(0, 100);
  const payload = viewer ? { viewer } : undefined;

  return whatsappJsonFetch(`/messages/${encodeURIComponent(messageId)}/read`, {
    method: "POST",
    ...(payload ? { body: JSON.stringify(payload) } : {}),
  });
}

/**
 * `POST /messages/reply/text` — session text reply (24h); optional **`quoted_message_id`** for WhatsApp reply threading.
 * @param {{ waId: string, body: string, quotedMessageId?: string | null }} params
 * @returns {Promise<unknown>}
 */
export async function sendWhatsAppTextReply(params) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(params.waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const bodyText = String(params.body ?? "");
  if (!bodyText.trim()) throw new Error("Message body is required");

  const payload = {
    wa_id: normalizedWaId,
    body: bodyText,
  };
  if (params.quotedMessageId) {
    payload.quoted_message_id = params.quotedMessageId;
  }

  return whatsappJsonFetch("/messages/reply/text", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** @typedef {'image' | 'video' | 'document' | 'audio' | 'sticker'} WhatsAppAttachFileKind */

const MESSAGES_ATTACH_FILE_PATHS = {
  image: "/messages/attach/image",
  video: "/messages/attach/video",
  document: "/messages/attach/document",
  audio: "/messages/attach/audio",
  sticker: "/messages/attach/sticker",
};

/**
 * Read a browser **`File`** as **`data:mime;base64,…`** for **`POST /messages/attach/*`** bodies.
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("File is required"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * `POST /messages/attach/{image|video|document|audio|sticker}` — session file send (24h); **`body`** is data URL or HTTPS URL.
 * @param {{
 *   waId: string,
 *   kind: WhatsAppAttachFileKind,
 *   file: File,
 *   filename?: string | null,
 *   caption?: string | null,
 *   quotedMessageId?: string | null
 * }} params
 * @returns {Promise<unknown>}
 */
export async function sendWhatsAppAttachFile(params) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(params.waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const kind = params.kind;
  const path = MESSAGES_ATTACH_FILE_PATHS[kind];
  if (!path) throw new Error("Invalid attachment kind");

  const file = params.file;
  if (!file) throw new Error("File is required");

  const bodyDataUrl = await readFileAsDataUrl(file);

  const defaultFilename =
    kind === "image"
      ? "image.jpg"
      : kind === "video"
        ? "video.mp4"
        : kind === "audio"
          ? file.name?.trim() || "audio.m4a"
          : kind === "sticker"
            ? "sticker.webp"
            : file.name?.trim() || "document.bin";

  const payload = {
    wa_id: normalizedWaId,
    body: bodyDataUrl,
    filename: (params.filename && String(params.filename).trim()) || defaultFilename,
  };

  const cap = params.caption != null ? String(params.caption).trim() : "";
  if (cap) payload.caption = cap;

  if (params.quotedMessageId) {
    payload.quoted_message_id = params.quotedMessageId;
  }

  return whatsappJsonFetch(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * `POST /messages/attach/location` — session location pin (24h).
 * @param {{
 *   waId: string,
 *   lat: string | number,
 *   lng: string | number,
 *   name?: string | null,
 *   address?: string | null,
 *   caption?: string | null,
 *   quotedMessageId?: string | null
 * }} params
 * @returns {Promise<unknown>}
 */
export async function sendWhatsAppAttachLocation(params) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(params.waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const lat = params.lat;
  const lng = params.lng;
  if (lat == null || lng == null || lat === "" || lng === "") {
    throw new Error("Latitude and longitude are required");
  }

  const payload = {
    wa_id: normalizedWaId,
    lat: String(lat),
    lng: String(lng),
  };
  if (params.name != null && String(params.name).trim()) payload.name = String(params.name).trim();
  if (params.address != null && String(params.address).trim()) {
    payload.address = String(params.address).trim();
  }
  const cap = params.caption != null ? String(params.caption).trim() : "";
  if (cap) payload.caption = cap;
  if (params.quotedMessageId) {
    payload.quoted_message_id = params.quotedMessageId;
  }

  return whatsappJsonFetch("/messages/attach/location", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * `POST /messages/attach/contact` — session contact card (24h).
 * @param {{
 *   waId: string,
 *   contacts: object[],
 *   body?: string | null,
 *   caption?: string | null,
 *   quotedMessageId?: string | null
 * }} params
 * @returns {Promise<unknown>}
 */
export async function sendWhatsAppAttachContact(params) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const normalizedWaId = normalizeWaId(params.waId);
  if (!normalizedWaId) throw new Error("WhatsApp ID is required");

  const contacts = params.contacts;
  if (!Array.isArray(contacts) || contacts.length === 0) {
    throw new Error("At least one contact is required");
  }

  const payload = {
    wa_id: normalizedWaId,
    contacts,
  };
  if (params.body != null && String(params.body).trim()) {
    payload.body = String(params.body).trim();
  }
  const cap = params.caption != null ? String(params.caption).trim() : "";
  if (cap) payload.caption = cap;
  if (params.quotedMessageId) {
    payload.quoted_message_id = params.quotedMessageId;
  }

  return whatsappJsonFetch("/messages/attach/contact", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * List all `whatsapp_read` rows with `read_status=unread` via `GET /db/whatsapp_read`.
 * @returns {Promise<object[]>}
 */
export async function listUnreadWhatsAppReadRows() {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const limit = 500;
  let offset = 0;
  const rows = [];

  while (offset <= 50000) {
    const query = new URLSearchParams({
      read_status: "unread",
      limit: String(limit),
      offset: String(offset),
    });
    const body = await whatsappJsonFetch(`/db/whatsapp_read?${query.toString()}`, {
      method: "GET",
    });
    const batch = Array.isArray(body?.data) ? body.data : [];
    rows.push(...batch);
    if (batch.length < limit) break;
    offset += limit;
  }

  return rows;
}

/**
 * Mark every unread inbound message read using `POST /messages/{message_id}/read` per row.
 * @param {(status: { processed_messages: number, total_messages: number, percent_complete: number, in_progress: boolean }) => void} [onProgress]
 */
export async function markAllWhatsAppMessagesAsRead(onProgress) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const readRows = await listUnreadWhatsAppReadRows();
  const messageIds = [
    ...new Set(readRows.map((row) => row?.message_id).filter((id) => id != null && id !== "")),
  ];
  const total = messageIds.length;
  let processed = 0;
  let successCount = 0;
  const errors = [];

  const reportProgress = () => {
    if (!onProgress) return;
    onProgress({
      processed_messages: processed,
      total_messages: total,
      percent_complete: total > 0 ? (processed / total) * 100 : 100,
      in_progress: processed < total,
    });
  };

  reportProgress();

  for (const messageId of messageIds) {
    try {
      await markWhatsAppMessageRead(messageId);
      successCount += 1;
    } catch (err) {
      errors.push({ message_id: messageId, error: err?.message || "Failed" });
    }
    processed += 1;
    reportProgress();
  }

  return {
    success: errors.length === 0 || successCount > 0,
    marked_count: successCount,
    total_messages: total,
    processed_messages: processed,
    percent_complete: 100,
    in_progress: false,
    errors,
    message:
      total === 0
        ? "No unread messages"
        : `Marked ${successCount} of ${total} messages as read`,
  };
}

/**
 * @param {{ phone: string, template: string, params: unknown[] }} payload
 */
/**
 * Batch job status (whatsapp_API openapi: GET /whatsapp/send-template/batch/{batch_id}).
 * @param {string} batchId
 * @returns {Promise<object>}
 */
export async function fetchWhatsAppBatchJobStatus(batchId) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const id = String(batchId || "").trim();
  if (!id) throw new Error("Missing batch id");

  return whatsappJsonFetch(
    `/whatsapp/send-template/batch/${encodeURIComponent(id)}`,
    { method: "GET" }
  );
}

export async function sendWhatsAppTemplate(payload) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const body = {
    phone: payload.phone,
    template: payload.template,
    params: payload.params,
  };

  return whatsappJsonFetch("/whatsapp/send-template", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Interpret POST /whatsapp/send-template n8n-wrapped response.
 * @returns {{ ok: boolean, message?: string, detail?: unknown }}
 */
export function interpretSendTemplateResult(data) {
  const j = data?.items?.[0]?.json;
  if (!j || typeof j !== "object") {
    return { ok: false, message: "The messaging service returned an unexpected response. Please try again or contact support." };
  }

  if (j.error) {
    return { ok: false, message: String(j.error), detail: j };
  }

  const body = j.body;
  const paramsEmpty =
    body &&
    typeof body === "object" &&
    Array.isArray(body.params) &&
    body.params.length === 0;

  if (paramsEmpty && j.sent !== true && !j.id) {
    return {
      ok: false,
      message: "This template could not be confirmed as sent. Try again, or contact support if it keeps happening.",
      detail: j,
    };
  }

  if (j.sent === true || j.id) {
    return { ok: true, detail: j };
  }

  return {
    ok: false,
    message: j.message || j.msg || "Send did not complete successfully.",
    detail: j,
  };
}

/** Approved DOCUMENT-header template: HEADER PDF + BODY `⇨  {{1}}`. */
export const START_TEMPLATE_DOCUMENT_PDF = "start_template_document_pdf";

/**
 * Convert a Blob/File to a `data:<mime>;base64,…` URI for POST /whatsapp/uploadMedia.
 * @param {Blob} blob
 * @param {string} [mimeFallback]
 * @returns {Promise<string>}
 */
export function blobToDataUrl(blob, mimeFallback = "application/pdf") {
  if (!(blob instanceof Blob)) {
    return Promise.reject(new Error("Expected a file to convert."));
  }
  const typed =
    blob.type && String(blob.type).trim()
      ? blob
      : new Blob([blob], { type: mimeFallback });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "").trim();
      if (!result.startsWith("data:")) {
        reject(new Error("Could not encode the PDF for WhatsApp."));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error("Could not read the PDF for WhatsApp."));
    reader.readAsDataURL(typed);
  });
}

/**
 * Upload a file/URL for a DOCUMENT/IMAGE/VIDEO template header.
 * JSON body: `{ body: "https://…" | "data:application/pdf;base64,…", filename? }`.
 * @param {{ body: string, filename?: string }} payload
 */
export async function uploadWhatsAppMedia(payload) {
  const cfg = getWhatsAppApiConfigStatus();
  if (!cfg.ok) throw new Error(cfg.message);

  const body = String(payload?.body || "").trim();
  if (!body) throw new Error("Missing media to upload.");

  const requestBody = { body };
  const filename = String(payload?.filename || "").trim();
  if (filename) requestBody.filename = filename;

  return whatsappJsonFetch("/whatsapp/uploadMedia", {
    method: "POST",
    body: JSON.stringify(requestBody),
  });
}

/**
 * Interpret POST /whatsapp/uploadMedia n8n-wrapped response.
 * @returns {{
 *   ok: boolean,
 *   message?: string,
 *   mediaId?: string,
 *   filename?: string,
 *   templateHeaderParam?: object | null,
 *   detail?: unknown,
 * }}
 */
export function interpretUploadMediaResult(data) {
  const j = data?.items?.[0]?.json;
  if (!j || typeof j !== "object") {
    return {
      ok: false,
      message:
        "The messaging service returned an unexpected upload response. Please try again or contact support.",
    };
  }

  if (j.error) {
    return { ok: false, message: String(j.error), detail: j };
  }

  const mediaId = String(j.mediaId || j.media_id || "").trim();
  if (!mediaId) {
    return {
      ok: false,
      message: "The file uploaded but no media id was returned. Try again.",
      detail: j,
    };
  }

  return {
    ok: true,
    mediaId,
    filename: String(j.filename || "").trim(),
    templateHeaderParam: j.template_header_param || null,
    detail: j,
  };
}

function buildDocumentHeaderParam(mediaId, filename) {
  const document = { id: String(mediaId || "").trim() };
  const name = String(filename || "").trim();
  if (name) document.filename = name;
  return {
    type: "header",
    parameters: [{ type: "document", document }],
  };
}

/**
 * Upload a PDF then send a DOCUMENT-header template (invoice flow).
 * @param {{
 *   phone: string,
 *   pdfBlob: Blob,
 *   filename?: string,
 *   bodyText?: string,
 *   template?: string,
 * }} options
 * @returns {Promise<{ ok: boolean, message?: string, detail?: unknown }>}
 */
export async function sendWhatsAppPdfDocumentTemplate({
  phone,
  pdfBlob,
  filename = "document.pdf",
  bodyText = "",
  template = START_TEMPLATE_DOCUMENT_PDF,
}) {
  const dataUrl = await blobToDataUrl(pdfBlob);
  const uploadRaw = await uploadWhatsAppMedia({ body: dataUrl, filename });
  const upload = interpretUploadMediaResult(uploadRaw);
  if (!upload.ok) {
    return upload;
  }

  const headerParam =
    upload.templateHeaderParam && typeof upload.templateHeaderParam === "object"
      ? upload.templateHeaderParam
      : buildDocumentHeaderParam(upload.mediaId, filename || upload.filename);

  const params = [
    headerParam,
    {
      type: "body",
      parameters: [
        {
          type: "text",
          text: String(bodyText || "").trim() || "Invoice",
        },
      ],
    },
  ];

  const sendRaw = await sendWhatsAppTemplate({
    phone,
    template: String(template || START_TEMPLATE_DOCUMENT_PDF).trim(),
    params,
  });
  return interpretSendTemplateResult(sendRaw);
}
